import type { MutableRefObject, ReactNode } from "preact/compat";
import { createContext, useRef, useMemo, useContext } from "preact/compat";

type BeforeUnloadValue = {
	readonly showWarningRef: MutableRefObject<boolean>;
	readonly preventUnloadWarning: () => void;
};

const context = createContext<BeforeUnloadValue | undefined>(undefined);

function useBeforeUnloadContext(): BeforeUnloadValue {
	const value = useContext(context);
	if (value === undefined) {
		throw new Error("No context found");
	}
	return value;
}

function usePreventUnloadWarningCallback(): () => void {
	return useBeforeUnloadContext().preventUnloadWarning;
}

function useShowUnloadWarningRef(): MutableRefObject<boolean> {
	return useBeforeUnloadContext().showWarningRef;
}

type BeforeUnloadProviderProps = {
	readonly children: ReactNode;
};

function BeforeUnloadProvider({ children }: BeforeUnloadProviderProps) {
	const showWarningRef = useRef<boolean>(true);
	const value = useMemo(
		() => ({
			preventUnloadWarning: () => {
				showWarningRef.current = false;
			},
			showWarningRef,
		}),
		[showWarningRef],
	);
	return <context.Provider value={value}>{children}</context.Provider>;
}

export {
	useShowUnloadWarningRef,
	BeforeUnloadProvider,
	usePreventUnloadWarningCallback,
};
